import { Logo } from "@/components/branding/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import { EyeIcon, EyeOffIcon, Lock, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

export default function ClientLogin() {
  const { tenant: tenantFromPath } = useParams<{ tenant?: string }>();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
    subdomain: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selfRegistrationEnabled, setSelfRegistrationEnabled] = useState<boolean | null>(null);
  const navigate = useNavigate();

  const resolvedTenant =
    (tenantFromPath || "").trim() ||
    searchParams.get("tenant")?.trim() ||
    formData.subdomain.trim();

  useEffect(() => {
    const t = (tenantFromPath || "").trim() || searchParams.get("tenant")?.trim();
    if (t) {
      setClientTenant(t);
      setFormData((prev) => ({ ...prev, subdomain: t }));
    }
  }, [tenantFromPath, searchParams]);

  useEffect(() => {
    const sub = resolvedTenant;
    if (!sub) {
      setSelfRegistrationEnabled(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [data] = await clientAuthService.fetchTenantSettings(sub);
      if (!cancelled) {
        setSelfRegistrationEnabled(data?.selfRegistrationEnabled ?? false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedTenant]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.identifier || !formData.password) {
      toast({
        title: "Erro",
        description: "Preencha e-mail (ou telefone) e senha",
        variant: "destructive",
      });
      return;
    }
    const sub = formData.subdomain.trim() || resolvedTenant;
    if (!sub) {
      toast({
        title: "Erro",
        description: "Informe o subdomínio da organização (ou acesse pelo link com /sua-empresa/cliente/login)",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    const [data, error] = await clientAuthService.login({
      identifier: formData.identifier,
      password: formData.password,
      subdomain: sub,
      tenant: sub,
    });
    setIsLoading(false);
    if (error) return;
    if (data) {
      toast({ title: "Sucesso", description: "Login realizado com sucesso!" });
      navigate(`${getClientPortalBase()}/tickets`);
    }
  };

  useEffect(() => {
    if (clientAuthService.isAuthenticated()) {
      navigate(`${getClientPortalBase()}/tickets`);
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card shadow-lg rounded-lg p-8 w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo size={32} textClassName="text-2xl" />
          </div>
          <h1 className="text-2xl font-bold">Portal do Cliente</h1>
          <p className="text-muted-foreground">Acesse seus tickets da organização</p>
          {resolvedTenant && (
            <p className="mt-2 text-sm font-medium text-primary">
              Espaço: <span className="font-mono">{resolvedTenant}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!tenantFromPath?.trim() && (
            <div className="space-y-2">
              <label htmlFor="subdomain" className="block text-sm font-medium">
                Subdomínio da organização
              </label>
              <Input
                id="subdomain"
                type="text"
                placeholder="ex: bytefy"
                value={formData.subdomain}
                onChange={(e) => handleChange("subdomain", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Ou use o link direto: <span className="font-mono">/bytefy/cliente/login</span>
              </p>
            </div>
          )}
          <div className="space-y-2">
            <label htmlFor="identifier" className="block text-sm font-medium">
              E-mail ou telefone
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="identifier"
                type="text"
                placeholder="seu@email.com ou telefone"
                className="pl-10"
                value={formData.identifier}
                onChange={(e) => handleChange("identifier", e.target.value)}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium">
              Senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="pl-10 pr-10"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1 h-8 w-8 p-0"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Entrando..." : "Entrar"}
          </Button>
        </form>

        {resolvedTenant && selfRegistrationEnabled && (
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Novo por aqui?{" "}
            <Link to={`${getClientPortalBase()}/cadastre-se`} className="text-primary font-medium hover:underline">
              Criar conta
            </Link>
          </p>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          É atendente?{" "}
          <Link to="/login" className="text-primary hover:underline">
            Acesse aqui
          </Link>
        </p>
      </div>
    </div>
  );
}
